import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../styles/Register.css';
import { COLORS } from '../styles/colors';
import Dialog from '../components/Dialog';
import logoImage from '../assets/tapgo-logo.png';
import { verifyResetToken, resetPassword, requestPasswordReset } from '../utils/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  // 이메일 입력 상태 (토큰이 없을 경우 사용)
  const [resetEmail, setResetEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  // 비밀번호 상태
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  
  // 유효성 검사 오류 메시지 상태
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    passwordConfirm: '',
  });
  
  // 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    // 특수문자와 숫자를 포함한 8자리 이상 비밀번호 검증
    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };
  
  // 토큰 검증
  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        // 토큰이 없는 경우 이메일 입력 화면 표시
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await verifyResetToken(token);
        setIsTokenValid(true);
        setEmail(response.email || '');
        setIsLoading(false);
      } catch (error) {
        setError(error.message || '유효하지 않거나 만료된 토큰입니다.');
        setIsLoading(false);
      }
    };
    
    checkToken();
  }, [token]);
  
  // 비밀번호 변경 시 확인
  useEffect(() => {
    if (password) {
      // 비밀번호 유효성 검사
      if (!validatePassword(password)) {
        setValidationErrors(prev => ({
          ...prev,
          password: '비밀번호는 특수문자와 숫자를 포함한 8자리 이상이어야 합니다.'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          password: ''
        }));
      }
    }
    
    // 비밀번호 확인 필드가 있을 경우 일치 여부 확인
    if (passwordConfirm) {
      if (password !== passwordConfirm) {
        setValidationErrors(prev => ({
          ...prev,
          passwordConfirm: '비밀번호가 일치하지 않습니다.'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          passwordConfirm: ''
        }));
      }
    }
  }, [password, passwordConfirm]);
  
  // 이메일로 비밀번호 재설정 링크 전송 핸들러
  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    
    if (!resetEmail || !resetEmail.includes('@')) {
      setDialogMessage('유효한 이메일 주소를 입력해주세요.');
      setShowDialog(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // 비밀번호 재설정 이메일 전송 API 호출
      await requestPasswordReset(resetEmail);
      
      setEmailSent(true);
      setDialogMessage('비밀번호 재설정 링크가 이메일로 전송되었습니다. 이메일을 확인해주세요.');
      setShowDialog(true);
    } catch (error) {
      console.error('비밀번호 재설정 이메일 전송 실패:', error);
      setDialogMessage(error.message || '이메일 전송 중 오류가 발생했습니다.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 비밀번호 재설정 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 비밀번호 유효성 검사
    if (!validatePassword(password)) {
      setDialogMessage('비밀번호는 특수문자와 숫자를 포함한 8자리 이상이어야 합니다.');
      setShowDialog(true);
      return;
    }
    
    // 비밀번호 일치 검사
    if (password !== passwordConfirm) {
      setDialogMessage('비밀번호가 일치하지 않습니다.');
      setShowDialog(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // 비밀번호 재설정 API 호출
      await resetPassword(token, password);
      
      // 성공 처리
      setResetSuccess(true);
      setDialogMessage('비밀번호가 성공적으로 재설정되었습니다. 앱에서 새 비밀번호로 로그인하세요.');
      setShowDialog(true);
    } catch (error) {
      console.error('비밀번호 재설정 실패:', error);
      
      // 오류 메시지 표시
      setDialogMessage(error.message || '비밀번호 재설정 중 오류가 발생했습니다.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 다이얼로그 닫기 핸들러
  const handleDialogClose = () => {
    setShowDialog(false);
    
    // 비밀번호 재설정이 완료되었을 때만 로그인 페이지로 이동
    if (resetSuccess) {
      navigate('/login', { replace: true }); // replace: true를 사용하여 뒤로가기 방지
    }
  };
  
  // 로딩 화면
  if (isLoading) {
    return (
      <>
        <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
          <div className="appBarLogo">
            <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
          </div>
        </div>
        
        <div className="registerContainer">
          <div className="loadingContainer">
            <div className="loadingSpinner"></div>
            <p>처리 중입니다...</p>
          </div>
        </div>
      </>
    );
  }
  
  // 오류 화면
  if (error || (token && !isTokenValid)) {
    return (
      <>
        <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
          <div className="appBarLogo">
            <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
          </div>
        </div>
        
        <div className="registerContainer">
          <div className="errorContainer">
            <div className="errorIcon">✕</div>
            <h2 className="errorTitle">오류가 발생했습니다</h2>
            <p className="errorMessage">{error || '유효하지 않거나 만료된 토큰입니다.'}</p>
            <button 
              className="registerButton" 
              onClick={() => navigate('/reset-password')}
              style={{ marginTop: '20px', marginBottom: '10px' }}
            >
              비밀번호 재설정 다시 시도
            </button>
            <button 
              className="registerButton" 
              onClick={() => navigate('/login')}
              style={{ marginTop: '10px', backgroundColor: '#6C757D' }}
            >
              로그인 페이지로 이동
            </button>
          </div>
        </div>
      </>
    );
  }
  
  // 비밀번호 재설정 성공 화면
  if (resetSuccess) {
    return (
      <>
        <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
          <div className="appBarLogo">
            <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
          </div>
        </div>
        
        <div className="registerContainer">
          <div className="registrationSuccessContainer">
            <div className="successIcon">✓</div>
            <h2 className="successTitle">비밀번호 재설정 완료</h2>
            <p className="successMessage">앱에서 새 비밀번호로 로그인하세요</p>
          </div>
        </div>
        
        {/* 다이얼로그 */}
        {showDialog && (
          <Dialog message={dialogMessage} onClose={handleDialogClose} />
        )}
      </>
    );
  }
  
  // 이메일로 비밀번호 재설정 링크 요청 화면 (토큰이 없을 경우)
  if (!token) {
    return (
      <>
        <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
          <div className="appBarLogo">
            <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
          </div>
        </div>
        
        <div className="registerContainer">
          <h2 className="registerTitle">비밀번호 찾기</h2>
          
          {emailSent ? (
            <div className="registrationSuccessContainer">
              <div className="successIcon">✓</div>
              <h2 className="successTitle">이메일 전송 완료</h2>
              <p className="successMessage">
                비밀번호 재설정 링크가 이메일로 전송되었습니다.
                <br />이메일을 확인해주세요.
              </p>
              <button 
                className="registerButton" 
                onClick={() => navigate('/login')}
                style={{ marginTop: '20px' }}
              >
                로그인 페이지로 이동
              </button>
            </div>
          ) : (
            <>
              <p className="resetDescription">
                가입한 이메일 주소를 입력하시면 비밀번호 재설정 링크를 보내드립니다.
              </p>
              
              <form onSubmit={handleSendResetEmail} className="registerForm">
                <div className="formGroup">
                  <label className="label">이메일</label>
                  <input
                    type="email"
                    className="input"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="가입한 이메일 주소"
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="registerButton"
                  style={{ marginTop: '30px' }}
                  disabled={isLoading}
                >
                  {isLoading ? '처리 중...' : '비밀번호 재설정 링크 받기'}
                </button>
              </form>
            </>
          )}
        </div>
        
        {/* 다이얼로그 */}
        {showDialog && (
          <Dialog message={dialogMessage} onClose={handleDialogClose} />
        )}
      </>
    );
  }
  
  // 비밀번호 재설정 폼
  return (
    <>
      <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
        <div className="appBarLogo">
          <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
        </div>
      </div>
      
      <div className="registerContainer">
        <h2 className="registerTitle">비밀번호 재설정</h2>
        <p className="resetDescription">
          {email && `${email} 계정의 `}새 비밀번호를 입력하세요
        </p>
        
        <form onSubmit={handleSubmit} className="registerForm">
          {/* 비밀번호 */}
          <div className="formGroup">
            <label className="label">새 비밀번호</label>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 (특수문자, 숫자 포함 8자 이상)"
              required
            />
            {validationErrors.password && (
              <p className="validationError">{validationErrors.password}</p>
            )}
          </div>
          
          {/* 비밀번호 확인 */}
          <div className="formGroup">
            <label className="label">비밀번호 확인</label>
            <input
              type="password"
              className="input"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="비밀번호 확인"
              required
            />
            {validationErrors.passwordConfirm && (
              <p className="validationError">{validationErrors.passwordConfirm}</p>
            )}
          </div>
          
          {/* 확인 버튼 */}
          <button 
            type="submit" 
            className="registerButton"
            style={{ marginTop: '40px' }}
            disabled={isLoading}
          >
            {isLoading ? '처리 중...' : '비밀번호 변경'}
          </button>
        </form>
      </div>
      
      {/* 다이얼로그 */}
      {showDialog && (
        <Dialog message={dialogMessage} onClose={handleDialogClose} />
      )}
    </>
  );
};

export default ResetPassword; 